import * as HeroIcons from '@heroicons/react/24/outline';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import React from 'react';
import { cn } from '../../../lib/utils';
import { MetricData } from '../services/analytics.service';
import CaseOriginDonutChart from './CaseOriginDonutChart';
import DonutChart from './DonutChart';
import NpsChart from './NpsChart';

interface MetricCardProps {
  metric: MetricData;
}

const MetricCard = ({ metric }: MetricCardProps) => {
  const getIcon = (iconName: string, color: string) => {
    // Map of icon names to Hero Icons components
    const iconMap: Record<string, any> = {
      ChartBarIcon: HeroIcons.ChartBarIcon,
      ArrowTrendingDownIcon: HeroIcons.ArrowTrendingDownIcon,
      UserIcon: HeroIcons.UserIcon,
      UserGroupIcon: HeroIcons.UserGroupIcon,
      ComputerDesktopIcon: HeroIcons.ComputerDesktopIcon,
      EnvelopeIcon: HeroIcons.EnvelopeIcon,
      ClockIcon: HeroIcons.ClockIcon,
      CheckCircleIcon: HeroIcons.CheckCircleIcon,
      HeartIcon: HeroIcons.HeartIcon,
      ShieldCheckIcon: HeroIcons.ShieldCheckIcon,
      CurrencyDollarIcon: HeroIcons.CurrencyDollarIcon,
      ArrowTrendingUpIcon: HeroIcons.ArrowTrendingUpIcon,
      PresentationChartLineIcon: HeroIcons.PresentationChartLineIcon,
      ChartBarSquareIcon: HeroIcons.ChartBarSquareIcon,
      BuildingOffice2Icon: HeroIcons.BuildingOffice2Icon,
      AcademicCapIcon: HeroIcons.AcademicCapIcon,
      UserPlusIcon: HeroIcons.UserPlusIcon,
      UserCheckIcon: HeroIcons.CheckCircleIcon,
      TicketIcon: HeroIcons.TicketIcon,
      StarIcon: HeroIcons.StarIcon,
      LifebuoyIcon: HeroIcons.LifebuoyIcon,
      PieChartIcon: HeroIcons.ChartPieIcon,
      CodeBracketIcon: HeroIcons.CodeBracketIcon,
      SparklesIcon: HeroIcons.SparklesIcon,
      ChatBubbleLeftRightIcon: HeroIcons.ChatBubbleLeftRightIcon,
      XCircleIcon: HeroIcons.XCircleIcon,
      UserMinusIcon: HeroIcons.UserMinusIcon,
      ArrowDownCircleIcon: HeroIcons.ArrowDownCircleIcon,
      CheckBadgeIcon: HeroIcons.CheckBadgeIcon,
      RocketLaunchIcon: HeroIcons.RocketLaunchIcon,
      // Add more icons as needed
    };

    const IconComponent = iconMap[iconName];
    if (!IconComponent) return null;

    return <IconComponent className="w-5 h-5 drop-shadow-sm" style={{ color }} />;
  };

  const formatValue = (
    value: number | object,
    displayType: string
  ): string | React.ReactElement => {
    switch (displayType) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value as number);
      case 'percentage':
        return `${(value as number).toFixed(1)}%`;
      case 'days':
        return `${(value as number).toFixed(1)} days`;
      case 'score': {
        return (value as number).toFixed(1);
      }
      case 'decimal': {
        const hours = value as number;
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;

        if (days > 0) {
          // Show formatted time with better readability - center aligned with highlighted breakdown
          return (
            <div className="flex flex-col items-center gap-1">
              <div className="font-black text-2xl">{hours.toFixed(1)}h</div>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200/50 dark:border-blue-700/50">
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400">{days}d</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                  {remainingHours.toFixed(1)}h
                </span>
              </div>
            </div>
          );
        } else {
          // Just show hours if less than 24h: "23.5h"
          return `${hours.toFixed(1)}h`;
        }
      }
      case 'nps-chart': {
        // Handle NPS score objects - return special marker for NPS chart
        const npsData = value as { npsScore: number; totalResponses: number };

        if (npsData && typeof npsData === 'object' && 'npsScore' in npsData) {
          return 'NPS_CHART'; // Special marker for NPS chart rendering
        }

        return 'Invalid NPS data';
      }
      case 'object': {
        // Handle health score objects - return special marker for donut chart
        const healthScore = value as { green: number; yellow: number; red: number };

        if (healthScore && typeof healthScore === 'object' && 'green' in healthScore) {
          return 'DONUT_CHART'; // Special marker for donut chart rendering
        }

        // Handle Case Origin breakdown objects - return special marker for case origin donut chart
        if (value && typeof value === 'object' && !('green' in value) && !('npsScore' in value)) {
          return 'CASE_ORIGIN_CHART'; // Special marker for case origin donut chart rendering
        }

        return 'Invalid data';
      }
      case 'number':
      default:
        return new Intl.NumberFormat('en-US').format(value as number);
    }
  };

  const getChangeIcon = (changeType?: string) => {
    switch (changeType) {
      case 'increase':
        return <ArrowUpIcon className="w-4 h-4" />;
      case 'decrease':
        return <ArrowDownIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getChangeColor = (changeType?: string) => {
    switch (changeType) {
      case 'increase':
        return 'text-green-600 dark:text-green-400';
      case 'decrease':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Check if this is a health score metric that should use donut chart
  const isHealthScore =
    metric.displayType === 'object' &&
    typeof metric.value === 'object' &&
    metric.value !== null &&
    'green' in metric.value;

  // Check if this is a Case Origin breakdown metric that should use case origin donut chart
  const isCaseOriginBreakdown =
    metric.displayType === 'object' &&
    typeof metric.value === 'object' &&
    metric.value !== null &&
    !('green' in metric.value) &&
    !('npsScore' in metric.value) &&
    metric.id === 'support-case-origin-breakdown';

  // Check if this is an NPS metric that should use NPS chart
  const isNpsChart =
    metric.displayType === 'nps-chart' &&
    typeof metric.value === 'object' &&
    metric.value !== null &&
    'npsScore' in metric.value;

  if (isHealthScore) {
    const healthScoreData = metric.value as { green: number; yellow: number; red: number };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow">
        <DonutChart data={healthScoreData} title={metric.name} />
      </div>
    );
  }

  if (isCaseOriginBreakdown) {
    const caseOriginData = metric.value as { [key: string]: number };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow">
        <CaseOriginDonutChart data={caseOriginData} title={metric.name} />
      </div>
    );
  }

  if (isNpsChart) {
    const npsData = metric.value as {
      npsScore: number;
      totalResponses: number;
      promoters: number;
      passives: number;
      detractors: number;
      promotersPercentage: number;
      passivesPercentage: number;
      detractorsPercentage: number;
    };

    return (
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 
                   shadow-lg hover:shadow-2xl transform transition-all duration-500 ease-out
                   hover:scale-[1.01] hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-600
                   bg-gradient-to-br from-white via-gray-50/30 to-gray-100/20 
                   dark:from-gray-800 dark:via-gray-800/50 dark:to-gray-900/20
                   backdrop-blur-sm relative overflow-hidden group"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        whileHover={{
          scale: 1.01,
          y: -2,
          transition: { duration: 0.2 },
        }}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-gray-100/10 dark:via-white/2 dark:to-gray-700/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {/* Header */}
        <div className="flex items-center justify-between mb-4 relative z-10">
          <motion.h3
            className="text-lg font-bold text-gray-900 dark:text-white"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            {metric.name}
          </motion.h3>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.3, type: 'spring', stiffness: 200 }}
          >
            {getIcon(metric.icon, metric.color)}
          </motion.div>
        </div>

        {/* NPS Chart with enhanced styling */}
        <div className="relative z-10">
          <NpsChart data={npsData} title={metric.name} color={metric.color} />
        </div>
      </motion.div>
    );
  }

  // Enhanced metric card with modern styling and animations
  return (
    <motion.div
      className={cn(
        'group relative overflow-hidden',
        'bg-gradient-to-br from-white via-white to-gray-50/50',
        'dark:from-gray-800 dark:via-gray-800 dark:to-gray-900/50',
        'rounded-2xl border border-gray-200/50 dark:border-gray-700/50',
        'backdrop-blur-sm shadow-lg hover:shadow-2xl',
        'transition-all duration-500 ease-out',
        'hover:scale-105 hover:-translate-y-1',
        'cursor-pointer'
      )}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
        transition: { duration: 0.2 },
      }}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/10 via-transparent to-purple-50/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative p-3">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3">
            {metric.icon && (
              <motion.div
                className={cn(
                  'w-9 h-9 flex items-center justify-center rounded-lg shadow-md',
                  'bg-gradient-to-br from-white to-gray-50 border-2',
                  'group-hover:scale-110 transition-transform duration-300'
                )}
                style={{
                  backgroundColor: `${metric.color}15`,
                  borderColor: `${metric.color}40`,
                  boxShadow: `0 4px 12px ${metric.color}20`,
                }}
                whileHover={{ rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.3 }}
              >
                {getIcon(metric.icon, metric.color)}
              </motion.div>
            )}
            <div className="flex-1 min-w-0">
              <motion.h3
                className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-1 truncate"
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {metric.name}
              </motion.h3>
              {metric.description && (
                <motion.p
                  className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed"
                  initial={{ opacity: 0.6 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  {metric.description}
                </motion.p>
              )}
            </div>
          </div>
          {metric.change !== undefined && (
            <motion.div
              className={cn(
                'flex items-center px-2 py-1 rounded-full text-xs font-semibold',
                getChangeColor(metric.changeType),
                'bg-white/80 shadow-sm border border-gray-200/50'
              )}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              {getChangeIcon(metric.changeType)}
              <span className="ml-1">{Math.abs(metric.change)}%</span>
            </motion.div>
          )}
        </div>

        <motion.div
          className="text-center min-h-[70px] flex items-center justify-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="text-2xl font-black bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700 dark:from-white dark:via-gray-100 dark:to-gray-200 bg-clip-text text-transparent mb-2 drop-shadow-md">
            {formatValue(metric.value, metric.displayType)}
          </div>
        </motion.div>

        {metric.chartData && metric.chartData.length > 0 && (
          <motion.div
            className="mt-6 pt-6 border-t border-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="h-16 flex items-end justify-between gap-1">
              {metric.chartData.slice(-7).map((point, index) => (
                <motion.div
                  key={index}
                  className="bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg w-full shadow-sm h-auto min-h-[6px]"
                  style={{
                    height: `${Math.max(6, (point.value / Math.max(...metric.chartData!.map(p => p.value))) * 64)}px`,
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                  whileHover={{
                    scaleY: 1.1,
                    backgroundColor: metric.color || '#3B82F6',
                  }}
                  title={`${point.date}: ${typeof formatValue(point.value, 'number') === 'string' ? formatValue(point.value, 'number') : point.value}`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Subtle shine effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[300%] transition-transform duration-1000" />
      </div>
    </motion.div>
  );
};

export default MetricCard;
